import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MultiSelect } from 'primeng/multiselect';
import { finalize } from 'rxjs';
import { DeliveryTypeEntity } from '../delivery-type.model';
import { DeliveryTypeService } from '../delivery-type.service';


@Component({
    selector: 'app-delivery-type-multi-select',
    imports: [FormsModule, MultiSelect],
    templateUrl: './delivery-type-multi-select.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeliveryTypeMultiSelectComponent {
    private destroyRef = inject(DestroyRef);
    private deliveryTypeService = inject(DeliveryTypeService);

    selectedDeliveryTypeKeys = model<number[]>([]);

    id = input<string>('deliveryTypeMultiSelect');
    placeHolder = input<string | undefined>('Select delivery types');

    subscriberKey = input.required<number | null>();

    invalid = input<boolean>(false);
    disabled = input<boolean>(false);

    options = signal<DeliveryTypeEntity[]>([]);
    isLoading = signal<boolean>(false);
    loadingChange = output<boolean>();

    blur = output();

    isDisabled = computed(() => this.isLoading() || this.disabled());
    isInvalid = computed(() => this.invalid() && !this.isDisabled());

    constructor() {
        /* Fetch Options when Input Changes */
        effect(() => {
            const subscriberKey = this.subscriberKey();

            if (subscriberKey == null) {
                this.options.set([]);
            } else {
                this.loadOptions(subscriberKey);
            }
        });

        /* Loading Change */
        effect(() => {
            this.loadingChange.emit(this.isLoading());
        });
    }

    loadOptions(subscriberKey: number) {
        this.isLoading.set(true);
        this.options.set([]);
        
        this.deliveryTypeService
            .getDeliveryTypes(subscriberKey)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => {
                    this.isLoading.set(false);
                })
            )
            .subscribe((deliveryTypes) => {
                const sorted = deliveryTypes.sort((a, b) => a.deliveryTypeName.localeCompare(b.deliveryTypeName));
                this.options.set(sorted);
            });
    }

    onModelChange(deliveryTypeKeys: number[] | null) {
        this.selectedDeliveryTypeKeys.set(deliveryTypeKeys ?? []);
    }
}
